import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { http } from '@/shared/api/httpClient';
import { env } from '@/shared/config/env';

import { buildOfflineCheckoutBody } from '../lib/checkoutBody';
import type {
  OfflineCheckoutInput,
  OfflineCheckoutResult,
  OfflineCouponList,
  OfflineCouponPreview,
  OfflineCustomerSnapshot,
  OfflineProductSearchParams,
  OfflineProductSearchResult,
  OfflineSaleSession,
} from '../types';
import { emptyOfflineSession } from '../types';

import { offlineOrderKeys } from './keys';
import {
  clearedSession,
  isClearCartAck,
  mapCheckoutResult,
  mapCouponList,
  mapCouponPreview,
  mapCustomerSnapshot,
  mapOfflineSession,
  mapProductSearch,
  unwrapStaffPayload,
} from './mapRemote';

function staffUrl(suffix = ''): string {
  const base = env.staffOfflineOrdersPath.replace(/\/$/, '');
  return suffix ? `${base}${suffix}` : base;
}

function searchParams(
  params: OfflineProductSearchParams,
): Record<string, string | number | boolean> {
  const query: Record<string, string | number | boolean> = { storeId: params.storeId };
  const text = params.query?.trim();
  if (text) query.query = text;
  if (params.categoryId) query.categoryId = params.categoryId;
  if (params.brandId) query.brandId = params.brandId;
  if (params.tags) query.tags = params.tags;
  if (params.minPrice !== undefined) query.minPrice = params.minPrice;
  if (params.maxPrice !== undefined) query.maxPrice = params.maxPrice;
  if (params.inStockOnly !== undefined) query.inStockOnly = params.inStockOnly;
  if (params.onOfferOnly !== undefined) query.onOfferOnly = params.onOfferOnly;
  if (params.sortBy) query.sortBy = params.sortBy;
  if (params.sortOrder) query.sortOrder = params.sortOrder;
  query.page = params.page ?? 1;
  query.limit = Math.min(100, Math.max(1, params.limit ?? 20));
  return query;
}

export async function fetchOfflineProducts(
  params: OfflineProductSearchParams,
): Promise<OfflineProductSearchResult> {
  const page = params.page ?? 1;
  const limit = Math.min(100, Math.max(1, params.limit ?? 20));
  const res = await http.get<unknown>(staffUrl('/products/search'), {
    params: searchParams({ ...params, page, limit }),
  });
  const payload = unwrapStaffPayload(res.data, res.status);
  return mapProductSearch(payload, page, limit);
}

export async function fetchOfflineCart(): Promise<OfflineSaleSession> {
  const res = await http.get<unknown>(staffUrl('/cart'));
  const payload = unwrapStaffPayload(res.data, res.status);
  return mapOfflineSession(payload, emptyOfflineSession());
}

async function readSession(
  res: { data: unknown; status: number },
  previous?: OfflineSaleSession,
): Promise<OfflineSaleSession> {
  const payload = unwrapStaffPayload(res.data, res.status);
  return mapOfflineSession(payload, previous);
}

export async function addOfflineCartItem(
  input: {
    storeId: string;
    productId: string;
    quantity: number;
  },
  previous?: OfflineSaleSession,
): Promise<OfflineSaleSession> {
  const res = await http.post<unknown>(staffUrl('/cart/items'), {
    storeId: input.storeId,
    productId: input.productId,
    quantity: input.quantity,
  });
  return readSession(res, previous);
}

export async function updateOfflineCartItem(
  productId: string,
  quantity: number,
  previous?: OfflineSaleSession,
): Promise<OfflineSaleSession> {
  const res = await http.patch<unknown>(staffUrl(`/cart/items/${encodeURIComponent(productId)}`), {
    quantity,
  });
  return readSession(res, previous);
}

export async function removeOfflineCartItem(
  productId: string,
  previous?: OfflineSaleSession,
): Promise<OfflineSaleSession> {
  const res = await http.delete<unknown>(staffUrl(`/cart/items/${encodeURIComponent(productId)}`));
  return readSession(res, previous);
}

export async function clearOfflineCart(): Promise<OfflineSaleSession> {
  const res = await http.delete<unknown>(staffUrl('/cart'));
  const payload = unwrapStaffPayload(res.data, res.status);
  if (isClearCartAck(payload)) return clearedSession();
  return mapOfflineSession(payload, emptyOfflineSession());
}

export async function identifyOfflineCustomer(
  phoneNumber: string,
  previous?: OfflineSaleSession,
): Promise<{
  session: OfflineSaleSession;
  customer: OfflineCustomerSnapshot | null;
}> {
  const res = await http.post<unknown>(staffUrl('/customer'), { phoneNumber });
  const payload = unwrapStaffPayload(res.data, res.status);
  return {
    session: mapOfflineSession(payload, previous),
    customer: mapCustomerSnapshot(payload),
  };
}

export async function clearOfflineCustomer(
  previous?: OfflineSaleSession,
): Promise<OfflineSaleSession> {
  const res = await http.delete<unknown>(staffUrl('/customer'));
  return readSession(res, previous);
}

export async function fetchOfflineCoupons(input: {
  phoneNumber: string;
  cartValue: number;
}): Promise<OfflineCouponList> {
  const res = await http.get<unknown>(staffUrl('/coupons'), {
    params: {
      phoneNumber: input.phoneNumber,
      cartValue: input.cartValue,
    },
  });
  return mapCouponList(unwrapStaffPayload(res.data, res.status));
}

export async function previewOfflineCoupon(couponCode: string): Promise<OfflineCouponPreview> {
  const res = await http.post<unknown>(staffUrl('/coupon/preview'), { couponCode });
  return mapCouponPreview(unwrapStaffPayload(res.data, res.status));
}

export async function applyOfflineCoupon(
  couponCode: string,
  previous?: OfflineSaleSession,
): Promise<OfflineSaleSession> {
  const res = await http.post<unknown>(staffUrl('/coupon'), { couponCode });
  return readSession(res, previous);
}

export async function removeOfflineCoupon(
  previous?: OfflineSaleSession,
): Promise<OfflineSaleSession> {
  const res = await http.delete<unknown>(staffUrl('/coupon'));
  return readSession(res, previous);
}

export async function checkoutOfflineOrder(
  input: OfflineCheckoutInput,
): Promise<OfflineCheckoutResult> {
  const res = await http.post<unknown>(staffUrl('/checkout'), buildOfflineCheckoutBody(input), {
    idempotency: true,
    headers: { 'Idempotency-Key': input.idempotencyKey },
  });
  return mapCheckoutResult(unwrapStaffPayload(res.data, res.status));
}

function useCartWriter() {
  const queryClient = useQueryClient();
  return (session: OfflineSaleSession) => {
    queryClient.setQueryData(offlineOrderKeys.cart(), session);
  };
}

export function useOfflineCartQuery() {
  return useQuery({
    queryKey: offlineOrderKeys.cart(),
    queryFn: fetchOfflineCart,
    staleTime: 0,
    retry: 1,
  });
}

export function useOfflineProductSearchQuery(
  params: OfflineProductSearchParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: offlineOrderKeys.search(params),
    queryFn: () => fetchOfflineProducts(params),
    enabled: (options?.enabled ?? true) && Boolean(params.storeId),
    placeholderData: keepPreviousData,
    retry: 1,
  });
}

export function useOfflineCouponsQuery(
  input: { phoneNumber: string; cartValue: number },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: offlineOrderKeys.coupons(input.phoneNumber, input.cartValue),
    queryFn: () => fetchOfflineCoupons(input),
    enabled: (options?.enabled ?? true) && input.phoneNumber.trim().length > 0,
    retry: 1,
  });
}

export function useAddOfflineCartItemMutation() {
  const queryClient = useQueryClient();
  const write = useCartWriter();
  return useMutation({
    mutationFn: (input: { storeId: string; productId: string; quantity: number }) =>
      addOfflineCartItem(input, queryClient.getQueryData(offlineOrderKeys.cart())),
    onSuccess: write,
  });
}

export function useUpdateOfflineCartItemMutation() {
  const queryClient = useQueryClient();
  const write = useCartWriter();
  return useMutation({
    mutationFn: ({ productId, quantity }: { productId: string; quantity: number }) =>
      updateOfflineCartItem(productId, quantity, queryClient.getQueryData(offlineOrderKeys.cart())),
    onSuccess: write,
  });
}

export function useRemoveOfflineCartItemMutation() {
  const queryClient = useQueryClient();
  const write = useCartWriter();
  return useMutation({
    mutationFn: (productId: string) =>
      removeOfflineCartItem(productId, queryClient.getQueryData(offlineOrderKeys.cart())),
    onSuccess: write,
  });
}

export function useClearOfflineCartMutation() {
  const write = useCartWriter();
  return useMutation({
    mutationFn: clearOfflineCart,
    onSuccess: write,
  });
}

export function useIdentifyOfflineCustomerMutation() {
  const queryClient = useQueryClient();
  const write = useCartWriter();
  return useMutation({
    mutationFn: (phoneNumber: string) =>
      identifyOfflineCustomer(
        phoneNumber,
        queryClient.getQueryData<OfflineSaleSession>(offlineOrderKeys.cart()),
      ),
    onSuccess: (result) => write(result.session),
  });
}

export function useClearOfflineCustomerMutation() {
  const queryClient = useQueryClient();
  const write = useCartWriter();
  return useMutation({
    mutationFn: () => clearOfflineCustomer(queryClient.getQueryData(offlineOrderKeys.cart())),
    onSuccess: write,
  });
}

export function useApplyOfflineCouponMutation() {
  const queryClient = useQueryClient();
  const write = useCartWriter();
  return useMutation({
    mutationFn: (couponCode: string) =>
      applyOfflineCoupon(couponCode, queryClient.getQueryData(offlineOrderKeys.cart())),
    onSuccess: write,
  });
}

export function useRemoveOfflineCouponMutation() {
  const queryClient = useQueryClient();
  const write = useCartWriter();
  return useMutation({
    mutationFn: () => removeOfflineCoupon(queryClient.getQueryData(offlineOrderKeys.cart())),
    onSuccess: write,
  });
}

export function usePreviewOfflineCouponMutation() {
  return useMutation({
    mutationFn: previewOfflineCoupon,
  });
}

export function useCheckoutOfflineOrderMutation() {
  return useMutation({
    mutationFn: checkoutOfflineOrder,
  });
}
